import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type CSSProperties, type Ref } from 'vue'
import type { TableColumnType, TableRowSelection } from './types'
import { columnKey } from './useTableData'

export function useFixedColumns<T>(columns: Readonly<Ref<TableColumnType<T>[]>>, selection: Readonly<Ref<TableRowSelection<T> | undefined>>, table: Ref<HTMLTableElement | null>) {
  const measuredWidths = ref<Record<string, number>>({})
  let observer: ResizeObserver | undefined
  const selectionWidth = computed(() => selection.value?.columnWidth ?? 48)
  const fixedSelection = computed(() => Boolean(selection.value?.fixed))
  function measure() {
    const widths: Record<string, number> = {}
    for (const cell of Array.from(table.value?.querySelectorAll<HTMLElement>('thead th[data-column-key]') ?? [])) {
      const width = cell.getBoundingClientRect().width
      if (width > 0) widths[cell.dataset.columnKey!] = width
    }
    if (JSON.stringify(widths) !== JSON.stringify(measuredWidths.value)) measuredWidths.value = widths
  }
  function width(column: TableColumnType<T>, index: number) {
    const measured = measuredWidths.value[columnKey(column, index)]
    if (measured !== undefined) return measured
    return typeof column.width === 'number' ? column.width : /^\d+(\.\d+)?(px)?$/.test(column.width ?? '') ? Number.parseFloat(column.width!) : 0
  }
  function colStyle(column: TableColumnType<T>, index: number): CSSProperties {
    const style: CSSProperties = {
      width: typeof column.width === 'number' ? `${column.width}px` : column.width,
      minWidth: typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth
    }
    if (column.fixed === true || column.fixed === 'left') {
      const offset = columns.value.slice(0, index).reduce((sum, item, i) => sum + (item.fixed === true || item.fixed === 'left' ? width(item, i) : 0), fixedSelection.value ? measuredWidths.value.__selection ?? Number.parseFloat(String(selectionWidth.value)) : 0)
      style.left = `${offset}px`
    } else if (column.fixed === 'right') {
      style.right = `${columns.value.slice(index + 1).reduce((sum, item, i) => sum + (item.fixed === 'right' ? width(item, index + i + 1) : 0), 0)}px`
    }
    return style
  }
  const selectionStyle = computed<CSSProperties>(() => ({ width: typeof selectionWidth.value === 'number' ? `${selectionWidth.value}px` : selectionWidth.value, left: fixedSelection.value ? '0px' : undefined, position: fixedSelection.value ? 'sticky' : undefined, zIndex: fixedSelection.value ? 3 : undefined }))
  onMounted(() => {
    measure()
    if (typeof ResizeObserver !== 'undefined' && table.value) { observer = new ResizeObserver(measure); observer.observe(table.value) }
  })
  watch(columns, async () => { await nextTick(); measure() }, { deep: true })
  onBeforeUnmount(() => observer?.disconnect())
  return { colStyle, selectionStyle }
}
